import React from 'react';

import { INSERT_BLOCK_SECTION } from '@atlaskit/editor-common/toolbar/keys';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import { Show } from '@atlaskit/editor-toolbar/show';
import { ToolbarButtonGroup } from '@atlaskit/editor-toolbar/toolbar-button-group';
import type { RegisterComponent } from '@atlaskit/editor-toolbar-model/types';

import type { NativeTabsToolbarPlugin } from './nativeTabsToolbarPluginType.ts';
import { TabsButton } from './ui/TabsButton.tsx';
import { TabsButtonSpotlight } from './ui/TabsButtonSpotlight.tsx';

/**
 * Hardcoded toolbar constants for the native tabs toolbar button.
 * These are kept within this plugin rather than in editor-common
 * because native tabs is a private Confluence feature.
 */
const TABS_GROUP = {
	key: 'tabs-group',
	type: 'group',
} as const;

const TABS_BUTTON = {
	key: 'tabs-button',
	type: 'button',
} as const;

/**
 * Rank of the tabs group within the INSERT_BLOCK_SECTION.
 * Positioned between layout (600) and synced-block (700).
 */
const TABS_GROUP_SECTION_RANK = 650;

/**
 * Rank of the tabs button within the tabs group.
 */
const TABS_BUTTON_GROUP_RANK = 100;

const getToolbarComponents = (
	api?: ExtractInjectionAPI<NativeTabsToolbarPlugin>,
): RegisterComponent[] => {
	return [
		{
			type: TABS_GROUP.type,
			key: TABS_GROUP.key,
			parents: [
				{
					type: INSERT_BLOCK_SECTION.type,
					key: INSERT_BLOCK_SECTION.key,
					rank: TABS_GROUP_SECTION_RANK,
				},
			],
			component: ({ children }: { children: React.ReactNode }) => (
				<Show above="md">
					<ToolbarButtonGroup>{children}</ToolbarButtonGroup>
				</Show>
			),
		},
		{
			type: TABS_BUTTON.type,
			key: TABS_BUTTON.key,
			parents: [
				{
					type: TABS_GROUP.type,
					key: TABS_GROUP.key,
					rank: TABS_BUTTON_GROUP_RANK,
				},
			],
			component: () => (
				<TabsButtonSpotlight>
					<TabsButton api={api} />
				</TabsButtonSpotlight>
			),
		},
	];
};

export const nativeTabsToolbarPlugin: NativeTabsToolbarPlugin = ({ api }) => {
	api?.toolbar?.actions.registerComponents(getToolbarComponents(api));

	return {
		name: 'nativeTabsToolbar',
	};
};
